import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Open/closed state of the "Vacinas → Configuração" modal (US #61577).
 *
 * The modal is an OVERLAY on top of whatever page is showing — it is NOT a route
 * (RA-14). The three menu entry points (desktop sidebar, mobile drawer, home
 * quick-access) toggle it through this singleton, and `LayoutComponent` — present
 * on every page — hosts the component, mounting it only while `open$` is `true`.
 * No navigation happens either way, so the page behind it stays intact and visible
 * (dimmed) under the scrim, which is what C-1.2 asks for.
 *
 * Close-on-nav: the `LayoutComponent` host is re-created on every navigation. If
 * the user navigates while the overlay is open, the freshly mounted layout would
 * mount a SECOND modal instance. This singleton outlives every LayoutComponent, so
 * closing on `NavigationStart` guarantees the new layout mounts with
 * `open$ === false`.
 *
 * **Close-on-nav is the FOURTH way in-flight writes are lost, and the only one that is silent by
 * construction** (US #61577, OQ-25 — the PO DECIDED on 2026-09-09 to accept the silence).
 *
 * The Spec names two: the voluntary close, which C-1.7 guards with a confirmation, and the
 * session expiring (C-3.12), whose announcement belongs to the global session flow. A third
 * is the 401/unmount pair. This is the fourth, and it differs from all of them in WHERE it
 * enters: the close below writes `openState$` DIRECTLY, so it never passes through
 * `VaccinesConfigPageComponent.close()` — which is where the C-1.7 guard lives. The guard
 * therefore never runs, no confirmation appears, the component is unmounted, and its
 * `takeUntil(destroy$)` aborts every write still in flight. The manager is told nothing, and
 * learns what landed only on the next open (C-1.6 re-reads).
 *
 * It is reachable from the ordinary browser Back button, not only from a menu click.
 *
 * **The PO accepted this on 2026-09-09, and accepting it IS the repository's standard** — not a
 * gap left open: the overlays that came before this one (`HealthFacilitiesModalService`,
 * `CollectionQueuesModalService`) close on `NavigationStart` the same
 * way, and the app declares NO `CanDeactivate` guard anywhere. Warning about a navigation the user
 * already committed to was judged not to be the better answer, and the damage is bounded: the next
 * open re-reads the truth (C-1.6). The Spec now carries this as a declared limit under C-1.7,
 * instead of saying nothing about it.
 *
 * The behavior stays FIXED BY TEST (`vaccines-config-modal.service.spec.ts`, and the consequence in
 * `vaccines-config-page.component.spec.ts`): reversing the decision — asking first, or letting the
 * writes finish — would mean this service consulting the mounted component before closing, and it
 * has to come here and edit an assertion, never alter the product by accident.
 *
 * Mold: `CollectionQueuesModalService` (US #60630).
 */
@Injectable({
  providedIn: 'root'
})
export class VaccinesConfigModalService {
  private readonly openState$ = new BehaviorSubject<boolean>(false);

  /** Emits the current open state; replays the latest value to new subscribers. */
  readonly open$: Observable<boolean> = this.openState$.asObservable();

  constructor(private readonly router: Router) {
    // The subscription lives for the app lifetime — this is a root singleton, so
    // there is nothing to unsubscribe from.
    this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => this.close());
  }

  /** Opens the modal overlay (mounts the component via the layout host). */
  open(): void {
    this.openState$.next(true);
  }

  /** Closes the modal overlay (unmounts the component). No navigation. */
  close(): void {
    this.openState$.next(false);
  }
}
